import React, { useState, useEffect } from 'react';
import Button from '../Button';
import SelectBox from '../SelectBox';
import MultiSelectDropdown from '../MultiSelectDropdown';
import SwitchBox from '../SwitchBox';
import classes from './ScheduleForm.module.css';

/**
 * ScheduleForm Component
 * Modal form for creating/editing report schedules
 */
const ScheduleForm = ({ 
	isOpen, 
	onClose, 
	onSubmit, 
	initialData = null,
	availableWatchers = []
}) => {
	const [formData, setFormData] = useState({
		scheduleName: '',
		frequency: 'weekly',
		watcherFilter: null,
		showSubcategories: false,
		dayOfWeek: 0,  // Monday by default
		dayOfMonth: 1,
		isActive: true,
		hour: 8,  // 8 AM UTC by default
		minute: 0
	});

	const [selectedWatchers, setSelectedWatchers] = useState([]);

	// Helper function to round minute to nearest 15-minute interval
	const roundToNearest15 = (minute) => {
		const validMinutes = [0, 15, 30, 45];
		return validMinutes.reduce((prev, curr) => 
			Math.abs(curr - minute) < Math.abs(prev - minute) ? curr : prev
		);
	};

	// Load initial data when editing
	useEffect(() => {
		if (initialData) {
			setFormData({
				scheduleName: initialData.scheduleName || '',
				frequency: initialData.frequency || 'weekly',
				watcherFilter: initialData.watcherFilter || null,
				showSubcategories: initialData.showSubcategories || false,
				dayOfWeek: initialData.dayOfWeek !== null ? initialData.dayOfWeek : 0,
				dayOfMonth: initialData.dayOfMonth || 1,
				isActive: initialData.isActive !== undefined ? initialData.isActive : true,
				hour: initialData.hour !== undefined ? initialData.hour : 8,
				minute: initialData.minute !== undefined ? roundToNearest15(initialData.minute) : 0
			});

			// Set selected watchers
			if (initialData.watcherFilter) {
				const watcherIds = initialData.watcherFilter.split(',').map(w => parseInt(w.trim()));
				setSelectedWatchers(watcherIds);
			} else {
				setSelectedWatchers([]);
			}
		} else {
			// Reset form for new schedule
			setFormData({
				scheduleName: '',
				frequency: 'weekly',
				watcherFilter: null,
				showSubcategories: false,
				dayOfWeek: 0,
				dayOfMonth: 1,
				isActive: true,
				hour: 8,
				minute: 0
			});
			setSelectedWatchers([]);
		}
	}, [initialData, isOpen]);

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === 'checkbox' ? checked : value
		}));
	};

	const handleWatcherChange = (watchers) => {
		setSelectedWatchers(watchers);
		// Convert to comma-separated string or null
		const watcherFilter = watchers.length > 0 ? watchers.join(',') : null;
		setFormData(prev => ({
			...prev,
			watcherFilter
		}));
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		
		// Validation
		if (!formData.scheduleName.trim()) {
			alert('Please enter a schedule name');
			return;
		}

		// Prepare data for submission
		const submitData = {
			...formData,
			// Ensure numeric values
			dayOfWeek: formData.frequency === 'weekly' ? parseInt(formData.dayOfWeek) : null,
			dayOfMonth: formData.frequency === 'monthly' ? parseInt(formData.dayOfMonth) : null
		};

		onSubmit(submitData);
	};

	if (!isOpen) return null;

	const frequencyOptions = [
		{ value: 'daily', label: 'Daily' },
		{ value: 'weekly', label: 'Weekly' },
		{ value: 'monthly', label: 'Monthly' }
	];

	const dayOfWeekOptions = [
		{ value: '0', label: 'Monday' },
		{ value: '1', label: 'Tuesday' },
		{ value: '2', label: 'Wednesday' },
		{ value: '3', label: 'Thursday' },
		{ value: '4', label: 'Friday' },
		{ value: '5', label: 'Saturday' },
		{ value: '6', label: 'Sunday' }
	];

	// Generate day of month options (1-28)
	const dayOfMonthOptions = Array.from({ length: 28 }, (_, i) => ({
		value: String(i + 1),
		label: String(i + 1)
	}));

	// Generate hour options (0-23)
	const hourOptions = Array.from({ length: 24 }, (_, i) => ({
		value: String(i),
		label: String(i).padStart(2, '0')
	}));

	// Generate minute options (15-minute intervals: 00, 15, 30, 45)
	const minuteOptions = [0, 15, 30, 45].map(i => ({
		value: String(i),
		label: String(i).padStart(2, '0')
	}));

	return (
		<div className={classes.modalOverlay} onClick={onClose}>
			<div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
				<div className={classes.modalHeader}>
					<h2>{initialData ? 'Edit Schedule' : 'Create New Schedule'}</h2>
					<button className={classes.closeButton} onClick={onClose}>×</button>
				</div>

				<form onSubmit={handleSubmit} className={classes.form}>
					<div className={classes.formGroup}>
						<label htmlFor="scheduleName" className={classes.label}>
							Schedule Name *
						</label>
						<input
							type="text"
							id="scheduleName"
							name="scheduleName"
							value={formData.scheduleName}
							onChange={handleInputChange}
							className={classes.input}
							placeholder="e.g., Weekly Security Report"
							required
						/>
					</div>

					<div className={classes.formGroup}>
						<label htmlFor="frequency" className={classes.label}>
							Frequency *
						</label>
						<SelectBox
							name="frequency"
							value={formData.frequency}
							onChange={(e) => handleInputChange(e)}
						>
							{frequencyOptions.map(opt => (
								<option key={opt.value} value={opt.value}>{opt.label}</option>
							))}
						</SelectBox>
					</div>

					{formData.frequency === 'weekly' && (
						<div className={classes.formGroup}>
							<label htmlFor="dayOfWeek" className={classes.label}>
								Day of Week *
							</label>
							<SelectBox
								name="dayOfWeek"
								value={String(formData.dayOfWeek)}
								onChange={(e) => handleInputChange(e)}
							>
								{dayOfWeekOptions.map(opt => (
									<option key={opt.value} value={opt.value}>{opt.label}</option>
								))}
							</SelectBox>
						</div>
					)}

					{formData.frequency === 'monthly' && (
						<div className={classes.formGroup}>
							<label htmlFor="dayOfMonth" className={classes.label}>
								Day of Month *
							</label>
							<SelectBox
								name="dayOfMonth"
								value={String(formData.dayOfMonth)}
								onChange={(e) => handleInputChange(e)}
							>
								{dayOfMonthOptions.map(opt => (
									<option key={opt.value} value={opt.value}>{opt.label}</option>
								))}
							</SelectBox>
						</div>
					)}

					<div className={classes.formGroup}>
						<label htmlFor="hour" className={classes.label}>
							Time (UTC) *
						</label>
						<div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', maxWidth: '300px' }}>
							<div style={{ flex: '1 1 0', minWidth: 0 }}>
								<SelectBox
									name="hour"
									value={String(formData.hour)}
									onChange={(e) => handleInputChange(e)}
								>
									{hourOptions.map(opt => (
										<option key={opt.value} value={opt.value}>{opt.label}</option>
									))}
								</SelectBox>
							
							</div>
							<span style={{ fontSize: '20px', fontWeight: 'bold', paddingTop: '8px' }}>:</span>
							<div style={{ flex: '1 1 0', minWidth: 0 }}>
								<SelectBox
									name="minute"
									value={String(formData.minute)}
									onChange={(e) => handleInputChange(e)}
								>
									{minuteOptions.map(opt => (
										<option key={opt.value} value={opt.value}>{opt.label}</option>
									))}
								</SelectBox>
								
							</div>
						</div>
						
					</div>

					<div className={classes.formGroup}>
						<label className={classes.label}>
							Watchers
						</label>
						<MultiSelectDropdown
							options={availableWatchers}
							selectedValues={selectedWatchers}
							onChange={handleWatcherChange}
							placeholder="All Watchers"
						/>
						
					</div>

					<div className={classes.formGroup}>
						<SwitchBox
							label="Enable Subcategory Breakdown"
							checked={formData.showSubcategories}
							onChange={(checked) => setFormData(prev => ({ ...prev, showSubcategories: checked }))}
						/>
						<small className={classes.helpText}>
							
						</small>
					</div>

					<div className={classes.formGroup}>
						<SwitchBox
							label="Active"
							checked={formData.isActive}
							onChange={(checked) => setFormData(prev => ({ ...prev, isActive: checked }))}
						/>
						<small className={classes.helpText}>
							Inactive schedules will not generate reports
						</small>
					</div>

					<div className={classes.formFooter}>
						<Button type="button" variant="secondary" onClick={onClose}>
							Cancel
						</Button>
						<Button type="submit" variant="primary">
							{initialData ? 'Update Schedule' : 'Create Schedule'}
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default ScheduleForm;

